import { useCallback, useEffect, useState } from "react";
import { Search, Pencil, Trash2, StickyNote, CalendarClock, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDateTime } from "../../lib/api";
import { STAGES, SOURCES, StageBadge, FuBadge, QuickActions, LeadModal, FollowUpModal, NoteModal, Modal, Timeline, inputCls, fmtINR, fmtDate } from "./CrmShared";

export default function CrmLeads({ refreshKey }) {
  const [data, setData] = useState({ leads: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [qd, setQd] = useState("");
  const [stage, setStage] = useState("");
  const [source, setSource] = useState("");
  const [fu, setFu] = useState("");
  const [sort, setSort] = useState("created_at");
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [fuLead, setFuLead] = useState(null);
  const [noteLead, setNoteLead] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { const t = setTimeout(() => setQd(q), 350); return () => clearTimeout(t); }, [q]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/store-admin/crm/leads", { params: { q: qd, stage, source, followup: fu, sort, order: sort === "name" ? "asc" : "desc", page, limit: 25 } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [qd, stage, source, fu, sort, page]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const del = async (l) => {
    if (!window.confirm(`Delete lead "${l.name}"?`)) return;
    try { await api.delete(`/store-admin/crm/leads/${l.id}`); toast.success("Lead deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const moveStage = async (l, st) => {
    try { await api.post(`/store-admin/crm/leads/${l.id}/stage`, { stage: st }); toast.success(`Moved to ${st}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openDetail = async (l) => {
    try { const r = await api.get(`/store-admin/crm/leads/${l.id}`); setDetail(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const iconBtn = "h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted shrink-0";

  return (
    <div className="space-y-4" data-testid="crm-leads">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="leads-search" className={`${inputCls} pl-9`} placeholder="Search name, phone, company…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        </div>
        <select data-testid="leads-filter-stage" className={`${inputCls} w-auto`} value={stage} onChange={(e) => { setStage(e.target.value); setPage(1); }}>
          <option value="">All stages</option>{STAGES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
        </select>
        <select data-testid="leads-filter-source" className={`${inputCls} w-auto`} value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }}>
          <option value="">All sources</option>{SOURCES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="leads-filter-followup" className={`${inputCls} w-auto`} value={fu} onChange={(e) => { setFu(e.target.value); setPage(1); }}>
          <option value="">Follow-up: any</option><option value="today">Due today</option><option value="overdue">Overdue</option><option value="upcoming">Upcoming</option>
        </select>
        <select data-testid="leads-sort" className={`${inputCls} w-auto`} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="created_at">Newest</option><option value="value">Deal value</option><option value="name">Name A–Z</option><option value="follow_up_date">Follow-up date</option><option value="updated_at">Recently updated</option>
        </select>
        <button onClick={() => { setEdit(null); setModalOpen(true); }} data-testid="leads-add"
          className="h-10 px-4 rounded-xl bg-foreground text-background text-xs font-extrabold">+ Add Lead</button>
      </div>

      <p className="text-xs text-muted-foreground font-bold" data-testid="leads-count">{data.total} lead(s)</p>

      {loading ? <div className="h-32 flex items-center justify-center"><div className="h-7 w-7 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>
        : data.leads.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="leads-empty">No leads yet. Add your first lead or share your website contact form.</div>
        : (
          <div className="space-y-2">
            {data.leads.map((l) => (
              <div key={l.id} className="rounded-2xl border border-border bg-background p-4 flex flex-wrap items-center gap-3" data-testid={`lead-row-${l.id}`}>
                <button onClick={() => openDetail(l)} className="flex-1 min-w-[160px] text-left" data-testid={`lead-open-${l.id}`}>
                  <p className="font-bold text-sm truncate">{l.name}{l.company ? <span className="font-medium text-muted-foreground"> · {l.company}</span> : ""}</p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{[l.phone, l.email, l.city].filter(Boolean).join(" · ") || "—"}</p>
                </button>
                <div className="flex flex-wrap items-center gap-2">
                  <StageBadge stage={l.stage} /><FuBadge lead={l} />
                  {l.value > 0 && <span className="text-sm font-extrabold" data-testid={`lead-value-${l.id}`}>{fmtINR(l.value)}</span>}
                  {l.assigned_to && <span className="text-[11px] font-bold text-muted-foreground">@{l.assigned_to}</span>}
                </div>
                <select value={l.stage} onChange={(e) => moveStage(l, e.target.value)} data-testid={`lead-stage-sel-${l.id}`}
                  className="h-8 rounded-lg border border-input bg-background px-2 text-xs font-bold">
                  {STAGES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                </select>
                <QuickActions name={l.name} phone={l.phone} whatsapp={l.whatsapp} email={l.email} />
                <div className="flex gap-1.5">
                  <button onClick={() => setNoteLead(l)} className={iconBtn} title="Add note" data-testid={`lead-note-${l.id}`}><StickyNote className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setFuLead(l)} className={iconBtn} title="Schedule follow-up" data-testid={`lead-fu-${l.id}`}><CalendarClock className="h-3.5 w-3.5" /></button>
                  <button onClick={() => { setEdit(l); setModalOpen(true); }} className={iconBtn} title="Edit" data-testid={`lead-edit-${l.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(l)} className={`${iconBtn} hover:bg-red-50 hover:text-red-600`} title="Delete" data-testid={`lead-del-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="leads-prev" className={iconBtn}><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-xs font-bold">{page} / {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="leads-next" className={iconBtn}><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}

      <LeadModal open={modalOpen} onClose={() => setModalOpen(false)} lead={edit} onSaved={load} />
      <FollowUpModal open={!!fuLead} onClose={() => setFuLead(null)} lead={fuLead} onSaved={load} />
      <NoteModal open={!!noteLead} onClose={() => setNoteLead(null)} title={`Note — ${noteLead?.name || ""}`}
        onSave={async (text) => { await api.post(`/store-admin/crm/leads/${noteLead.id}/notes`, { text }); load(); }} />

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.lead?.name || "Lead"} testid="lead-detail-modal">
        {detail && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2"><StageBadge stage={detail.lead.stage} /><FuBadge lead={detail.lead} />
              {detail.lead.value > 0 && <span className="font-extrabold text-sm">{fmtINR(detail.lead.value)}</span>}</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {[["Company", detail.lead.company], ["Phone", detail.lead.phone], ["WhatsApp", detail.lead.whatsapp],
                ["Email", detail.lead.email], ["City", detail.lead.city], ["Source", detail.lead.source],
                ["Assigned", detail.lead.assigned_to], ["Created", fmtDate(detail.lead.created_at)],
                ["Last contacted", fmtDate(detail.lead.last_contacted_at)]].map(([k, v]) => (
                <div key={k} className="min-w-0"><p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground">{k}</p><p className="font-semibold truncate">{v || "—"}</p></div>
              ))}
            </div>
            {detail.lead.requirement && <div><p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1">Requirement</p><p className="text-sm whitespace-pre-line">{detail.lead.requirement}</p></div>}
            <QuickActions name={detail.lead.name} phone={detail.lead.phone} whatsapp={detail.lead.whatsapp} email={detail.lead.email} />
            {detail.lead.notes?.length > 0 && (
              <div><p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2">Notes</p>
                <div className="space-y-2">{detail.lead.notes.slice().reverse().map((n, i) => (
                  <div key={i} className="rounded-xl bg-muted/50 p-3 text-sm"><p className="break-words">{n.text}</p><p className="text-[10px] text-muted-foreground mt-1">{fmtDate(n.at)}</p></div>
                ))}</div></div>
            )}
            {detail.deals?.length > 0 && (
              <div><p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2">Deals</p>
                {detail.deals.map((d) => <div key={d.id} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0"><span className="font-semibold">{d.name}</span><span className="font-bold">{fmtINR(d.value)} · {d.stage}</span></div>)}</div>
            )}
            <div><p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2">Activity</p><Timeline activities={detail.activities} /></div>
          </div>
        )}
      </Modal>
    </div>
  );
}
